// apps/web/src/pages/account/Security.tsx
//
// Plan 04-12b Task 3 — /account/security page.
//
// SURFACES (CONTEXT D-21 + plan must_haves):
//   - User info (email)
//   - MFA enrollment status (badge + "Enroll MFA" CTA if not enrolled)
//   - Recent audit events (last 20 from GET /api/auth/me/audit-events)
//   - Account actions:
//       - Sign out from all devices → POST /api/auth/sign-out-all
//         (Better-Auth's session-delete-all variant)
//       - Regenerate backup codes → fresh-auth re-auth modal → POST
//         /api/auth/backup-codes/regenerate → display new codes ONCE
//
// SECURITY:
//   - Audit events come from /me/audit-events which scopes by current
//     session user (Plan 04-12b me-route.ts).
//   - The re-auth modal sets x-fresh-auth: true on the regenerate POST
//     after the user supplies password + MFA. Plan 04-09 fresh-auth gate
//     accepts the header as the v1 mechanism (per CONTEXT D-12 deferred
//     polish item).
//   - Backup codes display ONCE in React state; cleared on dialog close.

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { api, ApiError } from "@/lib/api-client";
import { authClient, useSession } from "@/lib/auth-client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface AuditEvent {
  event: string;
  when: string;
  ip: string;
  success: boolean;
  errorCode?: string | null;
}

function formatWhen(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

export function Security(): React.ReactElement {
  const session = useSession() as {
    data: { user?: { id?: string; email?: string; twoFactorEnabled?: boolean } } | null;
    isPending: boolean;
  };
  const navigate = useNavigate();
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState<string | null>(null);

  // Re-auth modal state for regenerate-backup-codes.
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenPassword, setRegenPassword] = useState("");
  const [regenMfa, setRegenMfa] = useState("");
  const [regenSubmitting, setRegenSubmitting] = useState(false);
  const [regenError, setRegenError] = useState<string | null>(null);
  const [newBackupCodes, setNewBackupCodes] = useState<string[] | null>(null);

  const [signOutAllSubmitting, setSignOutAllSubmitting] = useState(false);
  const eventsLoaded = useRef(false);

  useEffect(() => {
    if (eventsLoaded.current) return;
    eventsLoaded.current = true;
    void (async () => {
      try {
        const result = await api<{ events: AuditEvent[] }>(
          "/api/auth/me/audit-events?limit=20",
          { method: "GET" },
        );
        setEvents(result.events);
      } catch (err) {
        setEventsError(
          err instanceof Error ? err.message : "Could not load recent activity",
        );
      } finally {
        setEventsLoading(false);
      }
    })();
  }, []);

  async function onSignOutAll(): Promise<void> {
    setSignOutAllSubmitting(true);
    try {
      // AUTH-11 "sign out from any device" — Better-Auth's /api/auth/revoke-sessions
      // tears down every session row owned by the current user (including the
      // current one). The cookie clears on response; on success we redirect to
      // sign-in. The server-side audit-log hook captures this via the path
      // mapping in apps/api/src/auth/hooks.ts (PATH_TO_EVENT['/revoke-sessions']
      // → 'signout'; the bulk variant is recorded under the same event name
      // as single-session signout, distinguishable by the request path in the
      // hook's metadata payload).
      await authClient.revokeSessions();
      navigate("/auth/sign-in");
    } catch {
      // Even if the API errors, the user expects "I'm signed out now"; the
      // local session is gone either way after Better-Auth clears the cookie.
      navigate("/auth/sign-in");
    } finally {
      setSignOutAllSubmitting(false);
    }
  }

  async function onRegenSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setRegenError(null);
    if (regenPassword.length === 0) {
      setRegenError("Enter your current password.");
      return;
    }
    setRegenSubmitting(true);
    try {
      // Step 1: re-authenticate (Better-Auth signIn.email with current creds).
      // This refreshes the session and marks it as "fresh-auth" in the next
      // 5-min window. Plan 04-09's route layer reads x-fresh-auth: true; we
      // set it explicitly so the regenerate POST succeeds.
      const email = session.data?.user?.email ?? "";
      if (email === "") {
        setRegenError("No active session — please sign in again.");
        return;
      }
      const signinResult = (await authClient.signIn.email({
        email,
        password: regenPassword,
      })) as {
        data:
          | { twoFactorRedirect?: boolean; user?: { id: string } }
          | null;
        error: { message?: string; code?: string } | null;
      };
      if (signinResult.error !== null && signinResult.error !== undefined) {
        setRegenError("Authentication failed");
        return;
      }
      // If MFA required, verify the supplied code now.
      if (
        signinResult.data?.twoFactorRedirect === true &&
        regenMfa.length === 0
      ) {
        setRegenError("Enter your authenticator code.");
        return;
      }
      if (signinResult.data?.twoFactorRedirect === true) {
        try {
          await api<{ ok: true }>("/api/auth/two-factor/verify-totp", {
            method: "POST",
            body: JSON.stringify({ code: regenMfa }),
          });
        } catch {
          setRegenError("That code didn't match.");
          return;
        }
      }
      // Step 2: regenerate.
      const result = await api<{ codes: string[]; codes_remaining: number }>(
        "/api/auth/backup-codes/regenerate",
        {
          method: "POST",
          headers: { "x-fresh-auth": "true" },
        },
      );
      setNewBackupCodes(result.codes);
      setRegenOpen(false);
      setRegenPassword("");
      setRegenMfa("");
    } catch (err) {
      if (err instanceof ApiError) {
        type ErrBody = { error?: { code?: string; message?: string } };
        const body = err.body as ErrBody | null;
        setRegenError(body?.error?.message ?? "Could not regenerate codes");
      } else {
        setRegenError(
          err instanceof Error ? err.message : "Could not regenerate codes",
        );
      }
    } finally {
      setRegenSubmitting(false);
    }
  }

  function onCloseRegenDialog(): void {
    setRegenOpen(false);
    setRegenPassword("");
    setRegenMfa("");
    setRegenError(null);
  }

  function onAcknowledgeNewCodes(): void {
    setNewBackupCodes(null);
  }

  function onDownloadNewCodes(): void {
    if (newBackupCodes === null) return;
    const text = [
      "The Recipe Spread — backup codes",
      "==================================",
      "",
      "Keep these codes somewhere safe. Each can be used once.",
      "",
      ...newBackupCodes,
      "",
      `Generated at ${new Date().toISOString()}`,
    ].join("\n");
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "recipe-spread-backup-codes.txt";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  const user = session.data?.user;
  const twoFactorEnabled = user?.twoFactorEnabled === true;

  return (
    <main className="mx-auto max-w-3xl space-y-6 p-8">
      <header>
        <h1 className="text-3xl font-semibold">Account security</h1>
        {user?.email !== undefined ? (
          <p className="text-sm text-muted-foreground">{user.email}</p>
        ) : null}
      </header>

      {/* MFA status section */}
      <Card>
        <CardHeader>
          <CardTitle>Two-factor authentication</CardTitle>
          <CardDescription>
            Protect your account by requiring a code from your authenticator
            app on every sign-in.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {twoFactorEnabled ? (
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-700">
                MFA enabled
              </span>
              <Button
                type="button"
                variant="outline"
                onClick={() => setRegenOpen(true)}
                disabled={regenSubmitting}
              >
                Regenerate backup codes
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">MFA not enabled</span>
              <Link
                to="/auth/mfa-enroll"
                className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                Enroll MFA
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent activity */}
      <Card>
        <CardHeader>
          <CardTitle>Recent activity</CardTitle>
          <CardDescription>Last 20 authentication events on this account.</CardDescription>
        </CardHeader>
        <CardContent>
          {eventsLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : eventsError !== null ? (
            <Alert variant="destructive">
              <AlertTitle>Could not load activity</AlertTitle>
              <AlertDescription>{eventsError}</AlertDescription>
            </Alert>
          ) : events.length === 0 ? (
            <p className="text-sm text-muted-foreground">No recent activity.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2">Event</th>
                    <th className="py-2">When</th>
                    <th className="py-2">IP</th>
                    <th className="py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev, idx) => (
                    <tr key={`${ev.event}-${ev.when}-${idx}`} className="border-b">
                      <td className="py-2 font-mono text-xs">{ev.event}</td>
                      <td className="py-2 text-muted-foreground">
                        {formatWhen(ev.when)}
                      </td>
                      <td className="py-2 font-mono text-xs">{ev.ip}</td>
                      <td className="py-2">
                        {ev.success ? (
                          <span className="text-green-700">success</span>
                        ) : (
                          <span className="text-red-700">
                            failed
                            {ev.errorCode !== null &&
                            ev.errorCode !== undefined &&
                            ev.errorCode !== ""
                              ? ` (${ev.errorCode})`
                              : ""}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Account actions */}
      <Card>
        <CardHeader>
          <CardTitle>Account actions</CardTitle>
        </CardHeader>
        <CardContent>
          <Button
            type="button"
            variant="outline"
            onClick={() => void onSignOutAll()}
            disabled={signOutAllSubmitting}
          >
            {signOutAllSubmitting
              ? "Signing out…"
              : "Sign out from all devices"}
          </Button>
        </CardContent>
      </Card>

      {/* Regenerate-codes re-auth dialog */}
      {regenOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
        >
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Confirm to regenerate</CardTitle>
              <CardDescription>
                Re-enter your password and authenticator code. All current
                backup codes will be invalidated.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={onRegenSubmit} className="space-y-4">
                {regenError !== null ? (
                  <Alert variant="destructive">
                    <AlertTitle>Could not confirm</AlertTitle>
                    <AlertDescription>{regenError}</AlertDescription>
                  </Alert>
                ) : null}
                <div className="space-y-1">
                  <Label htmlFor="regen-password">Current password</Label>
                  <Input
                    id="regen-password"
                    type="password"
                    autoComplete="current-password"
                    value={regenPassword}
                    onChange={(e) => setRegenPassword(e.target.value)}
                    disabled={regenSubmitting}
                    autoFocus
                  />
                </div>
                {twoFactorEnabled ? (
                  <div className="space-y-1">
                    <Label htmlFor="regen-mfa">Authenticator code</Label>
                    <Input
                      id="regen-mfa"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      placeholder="123456"
                      value={regenMfa}
                      onChange={(e) => setRegenMfa(e.target.value)}
                      disabled={regenSubmitting}
                    />
                  </div>
                ) : null}
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onCloseRegenDialog}
                    disabled={regenSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={regenSubmitting}>
                    {regenSubmitting ? "Confirming…" : "Regenerate"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      ) : null}

      {/* Display-once new backup codes */}
      {newBackupCodes !== null ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
        >
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Your new backup codes</CardTitle>
              <CardDescription>
                Save these now — they won't be shown again. Old backup codes
                are now invalid.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <pre
                data-testid="new-backup-codes-block"
                className="overflow-auto rounded-md bg-muted p-4 font-mono text-sm leading-loose"
              >
                {newBackupCodes.join("\n")}
              </pre>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Button type="button" variant="outline" onClick={onDownloadNewCodes}>
                  Download
                </Button>
                <Button type="button" onClick={onAcknowledgeNewCodes}>
                  I've saved them
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </main>
  );
}
